

import type * as React from "react"
import { Upload, FileText } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { MAX_UPLOAD_MB, isTooBig } from "@/lib/document-type"

interface FileUploaderProps {
  onFileSelect: (file: File | null) => void
  acceptedFileTypes: string
  selectedFile?: File | null
}

export function FileUploader({ onFileSelect, acceptedFileTypes, selectedFile }: FileUploaderProps) {
  const { toast } = useToast()

  const acceptedExtensions = acceptedFileTypes
    .split(",")
    .map((type) => type.trim().replace(".", "").toLowerCase())
    .filter(Boolean)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) {
      onFileSelect(null)
      return
    }

    if (isTooBig(file)) {
      toast({
        title: "Archivo demasiado grande",
        description: `El tamaño máximo permitido es ${MAX_UPLOAD_MB}MB.`,
        variant: "destructive",
      })
      onFileSelect(null)
      event.target.value = ""
      return
    }

    const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
    if (!acceptedExtensions.includes(extension)) {
      toast({
        title: "Tipo de archivo no soportado",
        description: `Tipos permitidos: ${acceptedFileTypes}`,
        variant: "destructive",
      })
      onFileSelect(null)
      event.target.value = ""
      return
    }

    onFileSelect(file)
  }

  return (
    <div className="flex items-center gap-2">
      {selectedFile && <FileText className="h-4 w-4 text-muted-foreground" />}
      <Input
        type="file"
        id="hidden-file-upload"
        className="hidden"
        onChange={handleFileChange}
        accept={acceptedFileTypes}
      />
      <Button variant="outline" asChild>
        <label htmlFor="hidden-file-upload" className="flex cursor-pointer items-center gap-2">
          <Upload className="h-4 w-4" />
          <span>{selectedFile ? selectedFile.name : "Seleccionar Archivo"}</span>
        </label>
      </Button>
    </div>
  )
}
